import { Navigate, useLocation } from 'react-router-dom';
import { useContext, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { TabActiveContext, useTabs } from '../context/TabsContext';

export const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const isActive = useContext(TabActiveContext);
  const { closeTab } = useTabs();

  const userRoles = user ? [user.role, ...(user.roles || [])].filter(Boolean) : [];
  const hasAllowedRole = allowedRoles.some((r) => userRoles.includes(r));
  const roleDenied = !!user && allowedRoles.length > 0 && !hasAllowedRole;

  // A tab for a path this role can never open is dead weight in the tab
  // strip forever (it can't be revisited) — drop it rather than leave a
  // permanent "ghost" tab behind. Done in an effect, not during render,
  // since closeTab() is a state update on a DIFFERENT component (TabsContext).
  // All hooks must run unconditionally (before any early `return` below) to
  // satisfy the Rules of Hooks across every render path of this component.
  useEffect(() => {
    if (roleDenied && isActive) closeTab(location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleDenied, isActive, location.pathname]);

  // If still loading, show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest-900 mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // If user data was passed via location state (from AuthCallback), use it
  if (location.state?.user && !user) {
    return children;
  }

  // If not authenticated, redirect to login. Backgrounded (inactive) tabs
  // must NOT render <Navigate> — its effect re-fires on every subsequent
  // navigation (since useNavigate's identity changes per route) and would
  // hijack the URL away from whatever tab the user actually switched to.
  if (!user) {
    if (!isActive) return null;
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (roleDenied) {
    if (!isActive) return null;
    // Redirect to appropriate dashboard based on role
    if (userRoles.includes('investor')) {
      return <Navigate to="/investor/dashboard" replace />;
    }
    if (userRoles.includes('customer')) {
      return <Navigate to="/customer/dashboard" replace />;
    }
    return <Navigate to="/staff/dashboard" replace />;
  }

  return children;
};

export const CustomerRoute = ({ children }) => {
  return (
    <ProtectedRoute allowedRoles={['customer', 'investor', 'admin']}>
      {children}
    </ProtectedRoute>
  );
};

export const StaffRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const isActive = useContext(TabActiveContext);
  const { closeTab } = useTabs();

  // Any authenticated staff role may access the staff portal. "Staff" = every
  // role that is NOT a customer or investor — this includes built-in roles
  // (branch_manager, relationship_manager, loan_officer, cashier, ...) AND any
  // custom role created in Roles & Permissions. Using a hardcoded allow-list
  // here previously caused an infinite redirect loop (blank page) for roles
  // not on the list.
  const role = user?.role;
  const roleDenied = role === 'investor' || role === 'customer';
  // Social contacts (self-registered, admin-approved) only get the Social menu.
  const socialOnlyDenied = role === 'social' && !location.pathname.startsWith('/staff/chat') && !location.pathname.startsWith('/staff/profile') && !location.pathname.startsWith('/staff/navigation') && !location.pathname.startsWith('/staff/contacts');

  useEffect(() => {
    if (roleDenied && isActive) closeTab(location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleDenied, isActive, location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-forest-900 mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (location.state?.user && !user) {
    return children;
  }
  if (!user) {
    if (!isActive) return null;
    return <Navigate to="/staff/login" state={{ from: location }} replace />;
  }

  if (role === 'investor') {
    if (!isActive) return null;
    return <Navigate to="/investor/dashboard" replace />;
  }
  if (role === 'customer') {
    if (!isActive) return null;
    return <Navigate to="/customer/dashboard" replace />;
  }
  if (socialOnlyDenied) {
    if (!isActive) return null;
    return <Navigate to="/staff/chat" replace />;
  }
  return children;
};

export const AdminRoute = ({ children }) => {
  return (
    <ProtectedRoute allowedRoles={['admin']}>
      {children}
    </ProtectedRoute>
  );
};
